import { FieldOption } from '@/forms/types/field-option';
import { FormSchema } from '@/forms/types/schema';
import { mergeSchemaAndValues } from '@/forms/utils/merge-schema-and-values';
import { ApplicationUser } from '@/types/application-user';
import { isUserTechnician } from '@/utils/users/is-user-technician';
import { UserFieldValues } from './field-values';

// TODO: platform admin should not have an organisation
const baseSchema = (): FormSchema<UserFieldValues> => ({
  firstName: {
    type: 'text',
    label: 'First name',
    placeholder: 'Enter first name',
    value: '',
    validation: { required: true, maxLength: 64 },
  },
  lastName: {
    type: 'text',
    label: 'Last name',
    placeholder: 'Enter last name',
    value: '',
    validation: { required: true, maxLength: 64 },
  },
  email: {
    type: 'text',
    label: 'Email',
    placeholder: 'Enter email address',
    value: '',
    validation: {
      required: true,
      maxLength: 256,
      pattern: {
        value: /\S+@\S+\.\S+/,
        message: 'Entered value does not match email format',
      },
    },
  },
  roleId: {
    type: 'select',
    label: 'Role',
    value: undefined,
    validation: { required: true },
  },
  organisationId: {
    type: 'select',
    label: 'Organisation',
    value: undefined,
  },
  permittedGroups: {
    type: 'multiselect',
    label: 'Allowed Groups',
    value: undefined,
  },
});

export const buildUserFormSchema = (
  {
    type,
    activeUser,
    activeUserIsPlatformAdmin,
    canAssignAnyOrganisation,
    isPlatformAdminRoleSelected,
    organisationName,
    organisationOptions,
    groupOptions,
    roleOptions,
    permittedGroupValueOptions,
  }: {
    type: 'create' | 'edit';
    activeUser?: ApplicationUser;
    activeUserIsPlatformAdmin?: boolean;
    canAssignAnyOrganisation: boolean;
    isPlatformAdminRoleSelected: boolean;
    organisationName: string;
    organisationOptions: FieldOption<number>[];
    groupOptions: FieldOption<number>[];
    roleOptions: FieldOption<number>[];
    permittedGroupValueOptions: FieldOption<number>[];
  },
  values?: UserFieldValues & { roleName: string },
): FormSchema<UserFieldValues> => {
  const schema = values
    ? mergeSchemaAndValues(baseSchema(), values)
    : baseSchema();

  schema.roleId = {
    ...schema.roleId,
    options: roleOptions,
  };
  schema.permittedGroups = {
    ...schema.permittedGroups,
    options: groupOptions,
    value: permittedGroupValueOptions,
  };
  if (canAssignAnyOrganisation) {
    schema.organisationId = {
      ...schema.organisationId,
      options: organisationOptions,
      validation: {
        ...schema.organisationId.validation,
        required: !isPlatformAdminRoleSelected,
      },
    };
  } else {
    schema.organisationId = {
      ...schema.organisationId,
      type: 'readonly',
      value: organisationName,
    };
  }

  if (type === 'edit') {
    // Email is readonly for MVP
    schema.email = {
      ...schema.email,
      type: 'readonly',
      value: values?.email ?? '',
    };

    // Role can only be changed by a Platform admin for MVP and a new account should be created if a new role is desired
    if (!activeUserIsPlatformAdmin) {
      schema.roleId = {
        ...schema.roleId,
        type: 'readonly',
        value: values?.roleName ?? '',
      };
    }
  }

  /**
   * Cannot assign a platform admin to an organisation
   */
  // TODO: Refactor to hidden
  if (isPlatformAdminRoleSelected) {
    schema.organisationId = {
      ...schema.organisationId,
      type: 'readonly',
      label: '',
      value: undefined,
    };
  }

  /**
   * Technicians can only view their assigned groups, not edit or add them
   */
  if (activeUser && isUserTechnician(activeUser)) {
    const groupNames = permittedGroupValueOptions
      .map((option) => option.label)
      .join(', ');
    schema.permittedGroups = {
      ...schema.permittedGroups,
      type: 'readonly',
      value: groupNames || 'No groups assigned',
    };
  }

  return schema;
};
